import React, {useEffect, useMemo, useState} from "react";
import {Form} from "react-router-dom";
import {toast} from "react-toastify";
import EditDivMutation from "./mutations/EditDivision";
import {SPORTS} from "../../constants/vars";
import {GlobalStyles} from "../../constants/styles";
import useHasFeature from "../common/hooks/useHasFeature";
import useThemeColors from "../organization/portal/useThemeColors";
import {alphaFromHex} from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";
import {AGE_GROUP_OPTIONS, COMPETITION_LEVEL_OPTIONS} from "../../constants/divisionAssignmentOptions";

export default function EditDivisionModal({
                                              show,
                                              onClose,
                                              divisions,
                                              setDivisions,
                                              divisionToEdit,
                                              waivers,
                                          }) {
    const [editDivisionMutation] = EditDivMutation();
    const colors = useThemeColors();
    const {t} = useTranslation();

    const {hasFeature} = useHasFeature();
    const canUseWaiver = hasFeature("waiver_management");

    const [formData, setFormData] = useState({
        id: "",
        name: "",
        sport: "",
        competition_level: "",
        age_group: "",
        mount_distance: "",
        pitch_limit: "",
        resting_games: "",
        waiver_ids: [],
        game_duration: "",
    });

    const [selectedSport, setSelectedSport] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (divisionToEdit) {
            const sportKey = divisionToEdit?.sport?.key?.toLowerCase?.() || "";

            setFormData({
                id: divisionToEdit.id,
                name: divisionToEdit.name || "",
                sport: sportKey,
                competition_level: divisionToEdit.competitionLevel || "",
                age_group: divisionToEdit.ageGroup || "",
                mount_distance: divisionToEdit.mountDistance ?? "",
                pitch_limit: divisionToEdit.pitchLimit ?? "",
                resting_games: divisionToEdit.restingGames ?? "",
                waiver_ids:
                    divisionToEdit?.waivers?.map((w) => w.id) ||
                    (divisionToEdit?.waiver?.id ? [divisionToEdit.waiver.id] : []),
                game_duration: divisionToEdit.gameDuration ?? "",
            });

            setSelectedSport(sportKey);
        }
    }, [divisionToEdit]);

    const modalStyles = useMemo(() => {
        return {
            wrap: {
                borderRadius: 16,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            titleWrap: {display: "flex", alignItems: "center", gap: 10},
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.04em",
                padding: "6px 10px",
                borderRadius: 999,
                color: colors.textOnPrimary,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
            },
            title: {
                margin: 0,
                fontSize: 16,
                fontWeight: 900,
                color: colors.textOnPrimary,
            },
            closeBtn: {
                width: 36,
                height: 36,
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            },
            body: {padding: 14, background: "#fff"},
            sectionTitle: {
                margin: "2px 0 10px",
                fontSize: 13,
                fontWeight: 900,
                color: "rgba(20,21,26,0.78)",
                letterSpacing: "0.02em",
                textTransform: "uppercase",
            },
            card: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
                marginBottom: 12,
            },
            label: {
                fontSize: 12.5,
                fontWeight: 800,
                color: "rgba(20,21,26,0.70)",
                marginBottom: 6,
            },
            input: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "10px 12px",
                fontWeight: 700,
                outline: "none",
            },
            select: {
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.12)",
                padding: "0px 12px",
                fontWeight: 800,
                outline: "none",
                backgroundColor: "#fff",
            },
            hint: {
                marginTop: 6,
                color: "rgba(20,21,26,0.55)",
                fontSize: 12.5,
                fontWeight: 600,
                lineHeight: 1.25,
            },
            footer: {
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 14px",
                background: "rgba(0,0,0,0.02)",
                borderTop: "1px solid rgba(0,0,0,0.06)",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
            },
            btnGhost: {
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.12)",
                color: "#14151a",
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },
        };
    }, [colors.primary, colors.textOnPrimary]);

    const selectClass = (value) =>
        `form-control kbola-select ${value ? "kbola-select--filled" : "kbola-select--placeholder"}`;
    const selectedWaiverTitles = useMemo(() => {
        const selectedSet = new Set((formData.waiver_ids || []).map((id) => String(id)));
        return (waivers || [])
            .filter((waiver) => selectedSet.has(String(waiver.id)))
            .map((waiver) => waiver.title);
    }, [formData.waiver_ids, waivers]);

    if (!show || !divisionToEdit) return null;

    return (
        <>
            <div
                className="modal fade show d-block"
                id="edit-div-modal"
                tabIndex="-1"
                role="dialog"
                aria-modal="true"
            >
                <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
                    <div className="modal-content" style={modalStyles.wrap}>
                        <Form
                            method="post"
                            onSubmit={async (e) => {
                                e.preventDefault();
                                setSaving(true);

                                try {
                                    const res = await editDivisionMutation({
                                        variables: {
                                            name: formData.name,
                                            sport: formData.sport,
                                            competitionLevel: formData.competition_level,
                                            ageGroup: formData.age_group,
                                            divisionId: formData.id,
                                            mountDistance: parseFloat(formData.mount_distance) || 0,
                                            pitchLimit: parseInt(formData.pitch_limit) || 0,
                                            restingGames: parseInt(formData.resting_games) || 0,
                                            waiverIds: formData.waiver_ids,
                                            gameDuration: parseInt(formData.game_duration) || 0,
                                        },
                                    });

                                    if (res.data?.editDivision?.division) {
                                        toast.success(t("editDivision.editedSuccess"), {
                                            autoClose: 3000,
                                            toastId: "div_edited",
                                            position: "top-center",
                                            closeOnClick: true,
                                        });

                                        setDivisions(
                                            divisions.map((div) =>
                                                div.id === res.data.editDivision.division.id
                                                    ? res.data.editDivision.division
                                                    : div
                                            )
                                        );

                                        onClose();
                                    }
                                } catch (err) {
                                    toast.error(getLocalizedGraphQLError(err, t, "editDivision.error"), {
                                        autoClose: 3000,
                                        toastId: "div_error",
                                        position: "top-center",
                                        closeOnClick: true,
                                    });
                                } finally {
                                    setSaving(false);
                                }
                            }}
                        >
                            <div className="modal-header" style={modalStyles.header}>
                                <div style={modalStyles.titleWrap}>
                                    <span style={modalStyles.badge}>{t("editDivision.editBadge")}</span>
                                    <h4 className="modal-title" style={modalStyles.title}>
                                        {t("editDivision.title")}
                                    </h4>
                                </div>

                                <button
                                    type="button"
                                    onClick={onClose}
                                    aria-label={t("editDivision.close")}
                                    style={modalStyles.closeBtn}
                                    disabled={saving}
                                    title={t("editDivision.close")}
                                >
                                    <span aria-hidden="true" style={{fontSize: 20, lineHeight: 1}}>
                                        &times;
                                    </span>
                                </button>
                            </div>

                            <div className="modal-body" style={modalStyles.body}>
                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>{t("editDivision.basics")}</div>

                                    <div className="form-group" style={{marginBottom: 12}}>
                                        <label style={modalStyles.label}>{t("editDivision.name")}</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            required
                                            value={formData.name}
                                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                                            style={modalStyles.input}
                                        />
                                    </div>

                                    <div className="form-group" style={{marginBottom: 12}}>
                                        <label style={modalStyles.label}>{t("editDivision.competitionLevel")}</label>
                                        <select className="form-control" value={formData.competition_level} onChange={(e) => setFormData({...formData, competition_level: e.target.value})} style={modalStyles.select}>
                                            <option value="">{t("editDivision.optionalSelection")}</option>
                                            {COMPETITION_LEVEL_OPTIONS.map((option) => <option key={option.value} value={option.value}>{t(`editDivision.competitionOptions.${option.value}`, option.label)}</option>)}
                                        </select>
                                    </div>

                                    <div className="form-group" style={{marginBottom: 12}}>
                                        <label style={modalStyles.label}>{t("editDivision.ageGroup")}</label>
                                        <select className="form-control" value={formData.age_group} onChange={(e) => setFormData({...formData, age_group: e.target.value})} style={modalStyles.select}>
                                            <option value="">{t("editDivision.optionalSelection")}</option>
                                            {AGE_GROUP_OPTIONS.map((option) => <option key={option.value} value={option.value}>{t(`editDivision.ageOptions.${option.value}`, option.label)}</option>)}
                                        </select>
                                    </div>

                                    <div className="form-group" style={{marginBottom: 0}}>
                                        <label style={modalStyles.label}>{t("editDivision.sport")}</label>
                                        <select
                                            className={selectClass(formData.sport)}
                                            required
                                            value={formData.sport}
                                            onChange={(e) => {
                                                setFormData({...formData, sport: e.target.value});
                                                setSelectedSport(e.target.value);
                                            }}
                                            style={modalStyles.select}
                                        >
                                            <option value="">{t("editDivision.chooseSport")}</option>
                                            {SPORTS.map((sport) => (
                                                <option key={sport.key} value={sport.key}>
                                                    {sport.value}
                                                </option>
                                            ))}
                                        </select>
                                        <div style={modalStyles.hint}>
                                            {t("editDivision.sportHint")}
                                        </div>
                                    </div>
                                </div>

                                <div style={modalStyles.card}>
                                    <div style={modalStyles.sectionTitle}>{t("editDivision.rules")}</div>

                                    {["baseball", "softball"].includes(selectedSport) && (
                                        <>
                                            <div className="form-group" style={{marginBottom: 12}}>
                                                <label style={modalStyles.label}>
                                                    {t("editDivision.mountDistance")}
                                                </label>
                                                <input
                                                    type="number"
                                                    step="any"
                                                    inputMode="decimal"
                                                    className="form-control"
                                                    placeholder={t("editDivision.mountDistancePlaceholder")}
                                                    value={formData.mount_distance}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            mount_distance: e.target.value,
                                                        })
                                                    }
                                                    style={modalStyles.input}
                                                />
                                            </div>

                                            <div className="form-group" style={{marginBottom: 12}}>
                                                <label style={modalStyles.label}>
                                                    {t("editDivision.pitchLimit")}
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    placeholder={t("editDivision.pitchLimitPlaceholder")}
                                                    value={formData.pitch_limit}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            pitch_limit: e.target.value,
                                                        })
                                                    }
                                                    style={modalStyles.input}
                                                />
                                            </div>

                                            <div className="form-group" style={{marginBottom: 12}}>
                                                <label style={modalStyles.label}>
                                                    {t("editDivision.restingGames")}
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    placeholder={t("editDivision.restingGamesPlaceholder")}
                                                    value={formData.resting_games}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            resting_games: e.target.value,
                                                        })
                                                    }
                                                    style={modalStyles.input}
                                                />
                                            </div>
                                        </>
                                    )}

                                    <div className="form-group" style={{marginBottom: 0}}>
                                        <label style={modalStyles.label}>
                                            {t("editDivision.gameDuration")}
                                        </label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            placeholder={t("editDivision.gameDurationPlaceholder")}
                                            value={formData.game_duration}
                                            onChange={(e) =>
                                                setFormData({...formData, game_duration: e.target.value})
                                            }
                                            style={modalStyles.input}
                                        />
                                    </div>
                                </div>

                                {canUseWaiver && (
                                    <div style={modalStyles.card}>
                                        <div style={modalStyles.sectionTitle}>{t("editDivision.policy")}</div>

                                        <div className="form-group" style={{marginBottom: 0}}>
                                            <label style={modalStyles.label}>
                                                {t("editDivision.waiverOptional")}
                                            </label>
                                            <div className="kbola-waiver-picker-wrap">
                                                <select
                                                    className={`${selectClass(formData.waiver_ids.length)} kbola-waiver-picker`}
                                                    value={formData.waiver_ids}
                                                    multiple
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            waiver_ids: Array.from(e.target.selectedOptions).map(
                                                                (opt) => opt.value
                                                            ),
                                                        })
                                                    }
                                                    style={modalStyles.select}
                                                    size={Math.min(6, Math.max(3, waivers?.length || 0))}
                                                >
                                                    {waivers?.map((waiver) => (
                                                        <option key={waiver.id} value={waiver.id}>
                                                            {waiver.title}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            {selectedWaiverTitles.length > 0 && (
                                                <div className="kbola-waiver-chip-row">
                                                    {selectedWaiverTitles.map((title, index) => (
                                                        <span className="kbola-waiver-chip" key={`${title}-${index}`}>
                                                            {title}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                            <div style={modalStyles.hint}>
                                                {t("editDivision.waiverHint")}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="modal-footer" style={modalStyles.footer}>
                                <button
                                    type="button"
                                    className="btn"
                                    onClick={onClose}
                                    style={{...modalStyles.btn, ...modalStyles.btnGhost}}
                                    disabled={saving}
                                >
                                    <i className="fas fa-times"/>
                                    {t("editDivision.close")}
                                </button>

                                <button
                                    type="submit"
                                    className="btn"
                                    style={{...modalStyles.btn, ...modalStyles.btnPrimary}}
                                    disabled={saving}
                                >
                                    <i className="fas fa-check"/>
                                    {saving ? t("editDivision.saving") : t("editDivision.save")}
                                </button>
                            </div>
                        </Form>
                    </div>
                </div>
            </div>

            <style>{`
                #edit-div-modal.modal {
                  background: rgba(0, 0, 0, 0.45);
                  backdrop-filter: blur(2px);
                }

                #edit-div-modal .modal-dialog {
                  max-height: 92vh;
                  display: flex;
                  flex-direction: column;
                  margin-top: 1.25rem;
                  margin-bottom: 1.25rem;
                }

                #edit-div-modal .modal-content {
                  flex: 1;
                  overflow: hidden;
                  display: flex;
                  flex-direction: column;
                }

                #edit-div-modal .modal-body {
                  overflow-y: auto;
                  max-height: 66vh;
                }

                #edit-div-modal .form-control:focus {
                  border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.9);
                  box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                }

                #edit-div-modal .kbola-select--placeholder {
                  color: rgba(20, 21, 26, 0.55) !important;
                }

                #edit-div-modal .kbola-select--filled {
                  color: rgba(20, 21, 26, 0.92) !important;
                }

                #edit-div-modal .kbola-waiver-picker-wrap {
                  border-radius: 12px;
                  border: 1px solid rgba(0, 0, 0, 0.11);
                  background: linear-gradient(180deg, rgba(255,255,255,0.94), rgba(247,248,250,0.92));
                  padding: 8px;
                }

                #edit-div-modal .kbola-waiver-picker {
                  min-height: 132px;
                  height: auto !important;
                  border: none !important;
                  background: transparent !important;
                  box-shadow: none !important;
                  padding: 0 !important;
                }

                #edit-div-modal .kbola-waiver-picker option {
                  border-radius: 9px;
                  margin: 2px 0;
                  padding: 9px 10px;
                  font-weight: 700;
                }

                #edit-div-modal .kbola-waiver-picker option:checked {
                  background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.72);
                  color: #14151a;
                }

                #edit-div-modal .kbola-waiver-picker-wrap:focus-within {
                  border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.95);
                  box-shadow: 0 0 0 0.22rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.22);
                }

                #edit-div-modal .kbola-waiver-chip-row {
                  display: flex;
                  flex-wrap: wrap;
                  gap: 6px;
                  margin-top: 8px;
                }

                #edit-div-modal .kbola-waiver-chip {
                  font-size: 11px;
                  font-weight: 800;
                  border-radius: 999px;
                  padding: 5px 9px;
                  color: rgba(20, 21, 26, 0.9);
                  border: 1px solid rgba(0, 0, 0, 0.1);
                  background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.42);
                  max-width: 100%;
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }

                #edit-div-modal .kbola-waiver-picker::-webkit-scrollbar {
                  width: 10px;
                }

                #edit-div-modal .kbola-waiver-picker::-webkit-scrollbar-thumb {
                  border-radius: 999px;
                  border: 2px solid rgba(255,255,255,0.95);
                  background: rgba(20, 21, 26, 0.24);
                }

                @media (max-width: 768px) {
                  #edit-div-modal .modal-dialog {
                    max-width: 95%;
                    margin: 0.9rem auto;
                  }
                  #edit-div-modal .modal-body {
                    max-height: 70vh;
                  }
                }
            `}</style>
        </>
    );
}
